import { computed } from 'vue'
import { freezeObject, isFunction } from '@/global/utils/utils'
import { api } from '@/global/services/api'
import { registeredTableHandlers } from '@/global/components/view-layouts/tabs-layout/config/registered-table-handlers'
import { showDetailsAction, deleteAction } from '@/global/components/table/helpers/tableActions'

const TABLE_KEY = 'roads'
const UI_VIEW = 'temporary_regulations_roads'

export function useTable (options) {
  const {
    trans,
    openRoadDialogFn,
    openInfoDialogFn,
    closeInfoDialogFn,
    roadTypes
  } = options || {}

  const fetchTableData = () => {
    const { fetchData } = registeredTableHandlers?.[TABLE_KEY] || {}
    if (!isFunction(fetchData)) {
      return
    }
    fetchData()
  }

  const onCreateNewRoad = () => {
    openRoadDialogFn({
      title: trans('road-maintenance/temporary_regulation.road_creation'),
      editId: null
    })
  }

  const onShowDetails = (event) => {
    openRoadDialogFn({
      title: trans('road-maintenance/temporary_regulation.road_update'),
      editId: event?.data?.id
    })
  }

  const deleteRoadHandler = async (event) => {
    try {
      const { id } = event?.data || {}
      if (!id) {
        return
      }

      await api('road-maintenance').delete(`roads/${id}`)
      fetchTableData()
      closeInfoDialogFn()
    }
    catch (exception) {
      console.error(exception)
    }
  }

  const onRoadDelete = (event) => {
    const { name } = event?.data || {}
    const nameTitle = name?.length > 20
      ? name.substring(0, 20) + '...'
      : name || ''

    openInfoDialogFn({
      title: trans('road-maintenance/temporary_regulation.road_delete') + (nameTitle ? ' - ' + nameTitle : ''),
      message: trans('road-maintenance/temporary_regulation.road_delete_confirmation'),
      onConfirm: () => deleteRoadHandler(event)
    })
  }

  const apiConfig = computed(() => ({
    get: {
      module: 'road-maintenance',
      route: 'roads'
    }
  }))

  const cellsConfig = freezeObject({
    type: {
      custom_display_value: (data) => {
        return trans(data?.type?.description || '')
      }
    },
    city_district: {
      custom_display_value: (data) => {
        return data?.city_district?.name || ''
      }
    }
  })

  const headers = computed(() => ([
    {
      label: trans('road-maintenance/temporary_regulation.name'),
      key: 'name',
      always_visible: true
    },
    {
      label: trans('road-maintenance/temporary_regulation.road_type'),
      key: 'type',
      always_visible: true
    },
    {
      label: trans('road-maintenance/temporary_regulation.city_district'),
      key: 'city_district',
      always_visible: true
    }
  ]))

  const actionsConfig = computed(() => ([
    showDetailsAction(trans, onShowDetails),
    deleteAction(trans, onRoadDelete)
  ]))

  const paginationConfig = freezeObject({
    perPageOptions: [50, 100],
    perPage: 50,
    currentPage: 1
  })

  const breadcrumbs = computed(() => ({
    items: [
      {
        text: trans('road-maintenance.settings')
      },
      {
        text: trans('road-maintenance/temporary_regulation/settings.roads'),
        bolded: true
      }
    ]
  }))

  const filtersConfig = computed(() => ({
    fields: [
      {
        key: 'name',
        label: trans('road-maintenance/excavation-dropdown-type.name')
      },
      {
        key: 'type_id',
        label: trans('road-maintenance/temporary_regulation.road_type'),
        type: 'autocomplete',
        items: roadTypes.value,
        autocomplete_options: {
          custom_display_value: (data) => {
            return trans(data?.description || '')
          }
        }
      },
      {
        key: 'city_district_id',
        label: trans('road-maintenance/temporary_regulation.city_district'),
        type: 'autocomplete',
        autocomplete_options: {
          module: 'road-maintenance',
          route: 'traffic-regulation/city-districts'
        }
      }
    ]
  }))

  const config = computed(() => ({
    tableConfig: {
      key: TABLE_KEY,
      uiView: UI_VIEW,
      apiConfig: apiConfig.value,
      filters_and_columns_visibility_config: {
        newBtnPosition: 'start',
        newClick: onCreateNewRoad
      },
      rowDblClick: onShowDetails,
      tableConfig: {
        cellsConfig,
        headers: headers.value,
        actionsConfig: actionsConfig.value,
        filtersConfig: filtersConfig.value,
        paginationConfig
      }
    },
    breadcrumbs: breadcrumbs.value
  }))

  return {
    config,
    fetchTableData
  }
}
